import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ShareButtons } from "@/components/ShareButtons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Star, Heart, ShoppingCart, Truck, ShieldCheck, Store, Languages, Loader2, MessageCircle, StoreIcon, PauseCircle, Bell, BadgeCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useI18n, TranslatedText } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { trackRecentlyViewed } from "@/lib/recentlyViewed";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlistIds } = useCart();
  const { user } = useAuth();
  const { startConversation } = useChat();
  const { t } = useI18n();
  const { format } = useCurrency();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const load = () => api.get(`/product/${id}`.replace("/product/", "/products/")).then((r) => setP(r.data));
  useEffect(() => { load(); trackRecentlyViewed(id); }, [id]);

  const sellerId = p?.seller?.seller_id;
  useEffect(() => {
    if (user && sellerId && user.user_id !== sellerId) {
      api.get(`/stores/${sellerId}/follow`).then((r) => setFollowing(!!r.data.following)).catch(() => {});
    }
  }, [user, sellerId]);

  const toggleFollow = async () => {
    if (!user) return navigate("/login");
    setFollowBusy(true);
    try {
      if (following) { await api.delete(`/stores/${sellerId}/follow`); setFollowing(false); toast.success("Unfollowed store"); }
      else { await api.post(`/stores/${sellerId}/follow`); setFollowing(true); toast.success("Following — new arrivals will show in your feed"); }
    } catch { toast.error("Something went wrong"); }
    setFollowBusy(false);
  };

  if (!p) return <Layout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  const price = p.discount_price || p.price;
  const inWish = wishlistIds?.includes(p.product_id);
  const storeUnavailable = !!p.store_unavailable;

  const doAddCart = async () => {
    if (!user) return navigate("/login");
    await addToCart(p.product_id, qty);
    toast.success("Added to cart");
  };
  const buyNow = async () => {
    if (!user) return navigate("/login");
    await addToCart(p.product_id, qty);
    navigate("/cart");
  };
  const submitReview = async () => {
    if (!user) return navigate("/login");
    await api.post("/reviews", { product_id: p.product_id, rating, comment });
    setComment("");
    toast.success("Review submitted");
    load();
  };

  const contactSeller = async () => {
    if (!user) return navigate("/login");
    const sellerId = p.seller?.seller_id;
    if (!sellerId) return toast.error("Seller unavailable");
    if (sellerId === user.user_id) return toast.error("This is your own product");
    try {
      await startConversation(sellerId, p.product_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to start chat");
    }
  };

  return (
    <Layout seo={{
      title: p.title,
      description: (p.description || `Buy ${p.title} on Xurray Mart — authentic African products delivered across Europe.`).replace(/<[^>]*>/g, "").slice(0, 200),
      image: p.images?.[0],
      type: "product",
      price: price,
      jsonLd: {
        "@context": "https://schema.org", "@type": "Product",
        name: p.title, image: p.images || [], description: (p.description || "").replace(/<[^>]*>/g, "").slice(0, 400),
        brand: p.seller?.business_name || "Xurray Mart",
        aggregateRating: (p.rating && p.review_count) ? { "@type": "AggregateRating", ratingValue: Number(p.rating).toFixed(1), reviewCount: p.review_count, bestRating: 5 } : undefined,
        offers: { "@type": "Offer", price: price, priceCurrency: "EUR",
          availability: (p.stock > 0 && !storeUnavailable) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
      },
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-3">
            <div className="aspect-square rounded-2xl overflow-hidden border border-border/50 bg-muted">
              <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" data-testid="product-main-image" />
            </div>
          </div>

          <div>
            <div className="flex gap-2 mb-3">
              {p.flash_sale && <Badge className="bg-destructive text-white">⚡ Flash Sale</Badge>}
              <Badge variant="outline">{p.country}</Badge>
              {p.category === "wine-alcohol" && <Badge data-testid="alcohol-badge-detail" className="bg-amber-600 hover:bg-amber-600 text-white">18+ · ID verification required</Badge>}
            </div>
            <TranslatedText as="h1" text={p.title} className="font-display font-bold text-2xl sm:text-3xl leading-tight" data-testid="product-title" />
            <div className="flex items-center gap-2 mt-2 text-sm">
              <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-gold text-gold" /><span className="font-semibold">{p.rating}</span></div>
              <span className="text-muted-foreground">· {p.review_count} reviews · {p.sold} sold</span>
            </div>

            <div className="flex items-baseline gap-3 mt-4">
              <span className="text-4xl font-black text-primary font-display" data-testid="product-price">{format(price)}</span>
              {p.discount_price && <span className="text-lg text-muted-foreground line-through">{format(p.price)}</span>}
            </div>

            <div className="mt-4 p-4 rounded-xl bg-muted/40">
              <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold">
                <Languages className="w-3.5 h-3.5 text-primary" />{t("description")}
              </div>
              <TranslatedText as="p" text={p.description} className="text-sm text-muted-foreground leading-relaxed" data-testid="product-description" />
            </div>

            {storeUnavailable && (
              <div data-testid="store-unavailable-banner" className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/20 p-4">
                <PauseCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">This store is briefly unavailable</p>
                  <p className="text-muted-foreground">{p.store_unavailable_reason || "Please check back soon."} You can still browse other stores in the meantime.</p>
                  <Button data-testid="follow-store-btn" onClick={toggleFollow} disabled={followBusy} size="sm" variant={following ? "outline" : "default"} className="rounded-full mt-3">
                    {followBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className={`w-4 h-4 mr-2 ${following ? "fill-current" : ""}`} />}
                    {following ? "Following — we'll notify you" : "Notify me when it's back"}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mt-5">
              <span className="text-sm font-medium">{t("quantity")}</span>
              <div className="flex items-center border rounded-full">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-1 text-lg">−</button>
                <span className="px-3 font-semibold" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-3 py-1 text-lg">+</button>
              </div>
              <span className="text-xs text-muted-foreground">{p.stock} {t("inStock")}</span>
            </div>

            <div className="flex gap-3 mt-6">
              <Button data-testid="add-to-cart-btn" onClick={doAddCart} disabled={storeUnavailable} size="lg" variant="outline" className="flex-1 rounded-full">
                <ShoppingCart className="w-4 h-4 mr-2" />{t("addToCart")}
              </Button>
              <Button data-testid="buy-now-btn" onClick={buyNow} disabled={storeUnavailable} size="lg" className="flex-1 rounded-full">{t("buyNow")}</Button>
              <Button data-testid="wishlist-detail-btn" onClick={() => user ? toggleWishlist(p.product_id) : navigate("/login")} size="icon" variant="outline" className="rounded-full h-11 w-11">
                <Heart className={`w-5 h-5 ${inWish ? "fill-primary text-primary" : ""}`} />
              </Button>
            </div>

            <div className="mt-6 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><Store className="w-4 h-4" />{t("soldBy")} {sellerId ? <Link to={`/store/${sellerId}`} data-testid="visit-store-link" className="font-semibold text-foreground hover:text-primary hover:underline">{p.seller?.business_name}</Link> : <span className="font-semibold text-foreground">{p.seller?.business_name}</span>}
                {p.seller?.seller_verified && (
                  <span data-testid="seller-verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-semibold">
                    <BadgeCheck className="w-3.5 h-3.5" />Verified
                  </span>
                )}
                {user?.user_id !== sellerId && (
                  <button data-testid="follow-store-inline-btn" onClick={toggleFollow} disabled={followBusy}
                    className={`ml-1 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${following ? "border-primary text-primary bg-primary/10" : "hover:bg-muted"}`}>
                    {followBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className={`w-3 h-3 ${following ? "fill-current" : ""}`} />}
                    {following ? "Following" : "Follow"}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground"><Truck className="w-4 h-4" />{(p.shipping_fee ?? p.seller?.shipping_fee ?? 0) > 0 ? `Shipping: ${format(Number(p.shipping_fee ?? p.seller?.shipping_fee))}${p.shipping_fee != null ? " / unit" : ""}` : t("freeShipping")}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4" />{t("securePayment")}</div>
              <Button data-testid="contact-seller-btn" onClick={contactSeller} variant="outline" size="sm" className="rounded-full mt-1">
                <MessageCircle className="w-4 h-4 mr-2" />Chat with seller
              </Button>
            </div>

            <ShareButtons title={p.title} url={`${process.env.REACT_APP_BACKEND_URL}/api/share/product/${id}`} className="mt-6 pt-6 border-t border-border" />
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-14 max-w-3xl">
          <h2 className="font-display font-bold text-2xl mb-4">{t("reviewsTitle")} ({p.reviews?.length || 0})</h2>
          <div className="p-4 rounded-xl border border-border/60 mb-6">
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}>
                  <Star className={`w-6 h-6 ${s <= rating ? "fill-gold text-gold" : "text-muted-foreground"}`} />
                </button>
              ))}
            </div>
            <Textarea data-testid="review-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("shareExperience")} className="mb-3" />
            <Button data-testid="submit-review-btn" onClick={submitReview}>{t("submitReview")}</Button>
          </div>
          <div className="space-y-4">
            {p.reviews?.map((r) => (
              <div key={r.review_id} className="border-b border-border/40 pb-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "fill-gold text-gold" : "text-muted-foreground"}`} />)}</div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
